// pages/AdminDashboard.js
// Original design + pagination + no refresh on tab change
// Last updated: December 2, 2025 - Added MAU/WAU/DAU

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://www.whydiditdie.com';
const USERS_PER_PAGE = 15;
const SUBSCRIPTIONS_PER_PAGE = 15;

const ADMIN_EMAILS = [
  'isabellaibeji@outlook.com',
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const dataFetchedRef = useRef(false);
  
  // Security state
  const [isVerified, setIsVerified] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(null);
  const [verifying, setVerifying] = useState(false);
  
  // Dashboard state - PERSISTED
  const [activeTab, setActiveTab] = useState(() => {
    return sessionStorage.getItem('admin_active_tab') || 'overview';
  });
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    proUsers: 0,
    trialUsers: 0,
    monthlyRevenue: 0,
    annualRevenue: 0,
    totalRevenue: 0,
    churnedUsers: 0,
    activeTrials: 0,
    expiredTrials: 0,
    signupsThisWeek: 0,
    signupsThisMonth: 0,
    arpu: 0,
    dau: 0,
    wau: 0,
    mau: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantDays, setGrantDays] = useState(30);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Pagination state
  const [usersPage, setUsersPage] = useState(1);
  const [subscriptionsPage, setSubscriptionsPage] = useState(1);

  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase());

  // Persist active tab
  useEffect(() => {
    sessionStorage.setItem('admin_active_tab', activeTab);
  }, [activeTab]);

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const storedToken = sessionStorage.getItem('admin_token');
      if (storedToken) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/admin/check`, {
            headers: { 'x-admin-token': storedToken }
          });
          const data = await response.json();
          if (data.valid) {
            setAdminToken(storedToken);
            setIsVerified(true);
          } else {
            sessionStorage.removeItem('admin_token');
          }
        } catch (error) {
          sessionStorage.removeItem('admin_token');
          logger.info('Admin session check failed, requiring PIN');
        }
      }
    };

    const lockoutData = localStorage.getItem('admin_lockout');
    if (lockoutData) {
      const { until } = JSON.parse(lockoutData);
      if (new Date(until) > new Date()) {
        setIsLocked(true);
        setLockoutTime(new Date(until));
      } else {
        localStorage.removeItem('admin_lockout');
      }
    }

    if (user && isAdmin) {
      checkExistingSession();
    }
  }, [user, isAdmin]);

  // Lockout countdown
  useEffect(() => {
    if (isLocked && lockoutTime) {
      const interval = setInterval(() => {
        if (new Date() > lockoutTime) {
          setIsLocked(false);
          setLockoutTime(null);
          setAttempts(0);
          localStorage.removeItem('admin_lockout');
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isLocked, lockoutTime]);

  // Fetch data when verified - ONLY ONCE
  useEffect(() => {
    if (!authLoading && user && isAdmin && isVerified && !dataFetchedRef.current) {
      dataFetchedRef.current = true;
      fetchAllData();
    }
  }, [authLoading, user, isAdmin, isVerified]);

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (isLocked || verifying) return;
    
    setVerifying(true);
    setPinError('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email, pin: pinInput })
      });
      
      const data = await response.json();
      
      if (data.status && data.token) {
        setAdminToken(data.token);
        setIsVerified(true);
        sessionStorage.setItem('admin_token', data.token);
      } else {
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        setPinInput('');
        
        if (newAttempts >= 5) {
          const lockUntil = new Date(Date.now() + 15 * 60 * 1000);
          setIsLocked(true);
          setLockoutTime(lockUntil);
          localStorage.setItem('admin_lockout', JSON.stringify({ until: lockUntil.toISOString() }));
          setPinError('Too many failed attempts. Locked for 15 minutes.');
        } else {
          setPinError(`Invalid PIN. ${5 - newAttempts} attempts remaining.`);
        }
      }
    } catch (error) {
      setPinError('Connection error. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleLogoutAdmin = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/logout`, {
        method: 'POST',
        headers: { 'x-admin-token': adminToken }
      });
    } catch (error) {}
    
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_active_tab');
    setAdminToken(null);
    setIsVerified(false);
    setPinInput('');
    dataFetchedRef.current = false;
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchUsers(), fetchSubscriptions(), fetchStats()]);
    setLoading(false);
  }, []);

  const handleRefresh = () => {
    dataFetchedRef.current = false;
    fetchAllData();
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      logger.error('Error fetching users:', error);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSubscriptions(data || []);
    } catch (error) {
      logger.error('Error fetching subscriptions:', error);
    }
  };

  const fetchStats = async () => {
    try {
      // Fetch from user_activity view for MAU/WAU/DAU
      const { data: userActivity } = await supabase.from('user_activity').select('*');
      
      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      // Calculate DAU, WAU, MAU from last_sign_in_at
      const dau = userActivity?.filter(u => 
        u.last_sign_in_at && new Date(u.last_sign_in_at) > oneDayAgo
      ).length || 0;
      
      const wau = userActivity?.filter(u => 
        u.last_sign_in_at && new Date(u.last_sign_in_at) > oneWeekAgo
      ).length || 0;
      
      const mau = userActivity?.filter(u => 
        u.last_sign_in_at && new Date(u.last_sign_in_at) > oneMonthAgo
      ).length || 0;

      const { data: profiles } = await supabase.from('profiles').select('plan_type, trial_end_date, created_at, email');
      
      const totalUsers = profiles?.length || 0;
      const proUsers = profiles?.filter(p => p.plan_type === 'pro' && !ADMIN_EMAILS.includes(p.email?.toLowerCase())).length || 0;
      const trialUsers = profiles?.filter(p => p.plan_type === 'free_trial').length || 0;
      
      const signupsThisWeek = profiles?.filter(p => new Date(p.created_at) > oneWeekAgo).length || 0;
      const signupsThisMonth = profiles?.filter(p => new Date(p.created_at) > oneMonthAgo).length || 0;
      
      const activeTrials = profiles?.filter(p => 
        p.plan_type === 'free_trial' && p.trial_end_date && new Date(p.trial_end_date) > now
      ).length || 0;
      const expiredTrials = trialUsers - activeTrials;

      const { data: allSubs } = await supabase
        .from('subscriptions')
        .select('amount, billing_cycle, status, email');
      
      const realActiveSubs = allSubs?.filter(s => 
        s.status === 'active' && !ADMIN_EMAILS.includes(s.email?.toLowerCase())
      ) || [];
      
      const monthlyRevenue = realActiveSubs
        .filter(s => s.billing_cycle === 'monthly')
        .reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
      
      const annualRevenue = realActiveSubs
        .filter(s => s.billing_cycle === 'annual')
        .reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);

      const { data: cancelled } = await supabase
        .from('subscriptions')
        .select('id, email')
        .eq('status', 'cancelled');
      
      const realChurned = cancelled?.filter(s => !ADMIN_EMAILS.includes(s.email?.toLowerCase())).length || 0;

      const totalRealRevenue = monthlyRevenue + annualRevenue;
      const arpu = proUsers > 0 ? totalRealRevenue / proUsers : 0;

      setStats({
        totalUsers, proUsers, trialUsers, 
        monthlyRevenue, annualRevenue,
        totalRevenue: totalRealRevenue,
        churnedUsers: realChurned,
        activeTrials, expiredTrials,
        signupsThisWeek, signupsThisMonth,
        arpu, dau, wau, mau
      });
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  };

  const handleGrantAccess = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + grantDays);

      await supabase.from('subscriptions').insert({
        user_id: selectedUser.id,
        email: selectedUser.email,
        plan: 'pro',
        billing_cycle: 'granted',
        amount: 0,
        status: 'active',
        starts_at: startDate.toISOString(),
        ends_at: endDate.toISOString(),
        auto_renew: false,
        paystack_reference: `grant_${Date.now()}`
      });

      await supabase.from('profiles').update({ 
        plan_type: 'pro',
        updated_at: new Date().toISOString()
      }).eq('id', selectedUser.id);

      window.alert(`Granted ${grantDays} days Pro access to ${selectedUser.email}`);
      setShowGrantModal(false);
      setSelectedUser(null);
      handleRefresh();
    } catch (error) {
      window.alert('Failed to grant access: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeAccess = async (userToRevoke) => {
    if (!window.confirm(`Are you sure you want to revoke Pro access for ${userToRevoke.email}?`)) return;

    setActionLoading(true);
    try {
      await supabase.from('subscriptions').update({ status: 'revoked' })
        .eq('email', userToRevoke.email).eq('status', 'active');

      await supabase.from('profiles').update({ 
        plan_type: 'free_trial',
        updated_at: new Date().toISOString()
      }).eq('id', userToRevoke.id);

      window.alert(`Revoked Pro access for ${userToRevoke.email}`);
      handleRefresh();
    } catch (error) {
      window.alert('Failed to revoke access');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendTrial = async (userToExtend) => {
    const days = window.prompt('How many days to add to trial?', '14');
    if (!days || isNaN(days)) return;

    setActionLoading(true);
    try {
      const currentEnd = userToExtend.trial_end_date 
        ? new Date(userToExtend.trial_end_date) 
        : new Date();
      
      const newEnd = new Date(currentEnd);
      newEnd.setDate(newEnd.getDate() + parseInt(days));

      await supabase.from('profiles').update({ 
        trial_end_date: newEnd.toISOString(),
        updated_at: new Date().toISOString()
      }).eq('id', userToExtend.id);

      window.alert(`Extended trial by ${days} days for ${userToExtend.email}`);
      handleRefresh();
    } catch (error) {
      window.alert('Failed to extend trial');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendSubscription = async (subscription) => {
    const days = window.prompt('How many days to extend?', '30');
    if (!days || isNaN(days)) return;

    setActionLoading(true);
    try {
      const currentEnd = new Date(subscription.ends_at);
      const newEnd = new Date(currentEnd);
      newEnd.setDate(newEnd.getDate() + parseInt(days));

      await supabase.from('subscriptions').update({ 
        ends_at: newEnd.toISOString(),
        updated_at: new Date().toISOString()
      }).eq('id', subscription.id);

      window.alert(`Extended subscription by ${days} days`);
      handleRefresh();
    } catch (error) {
      window.alert('Failed to extend subscription');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUsersPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (usersPage - 1) * USERS_PER_PAGE,
    usersPage * USERS_PER_PAGE
  );

  const totalSubsPages = Math.ceil(subscriptions.filter(s => !ADMIN_EMAILS.includes(s.email?.toLowerCase())).length / SUBSCRIPTIONS_PER_PAGE);
  const paginatedSubs = subscriptions
    .filter(s => !ADMIN_EMAILS.includes(s.email?.toLowerCase()))
    .slice(
      (subscriptionsPage - 1) * SUBSCRIPTIONS_PER_PAGE,
      subscriptionsPage * SUBSCRIPTIONS_PER_PAGE
    );

  useEffect(() => { setUsersPage(1); }, [searchQuery]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD'
    }).format(amount || 0);
  };

  const getTrialStatus = (u) => {
    if (u.plan_type === 'pro') return null;
    if (!u.trial_end_date) return { text: 'No date', color: 'text-gray-500' };
    const daysLeft = Math.ceil((new Date(u.trial_end_date) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { text: `${Math.abs(daysLeft)}d overdue`, color: 'text-red-400' };
    if (daysLeft <= 3) return { text: `${daysLeft}d left`, color: 'text-orange-400' };
    return { text: `${daysLeft}d left`, color: 'text-green-400' };
  };

  const Pagination = ({ current, total, onChange }) => {
    if (total <= 1) return null;
    return (
      <div className="flex items-center justify-center gap-2 mt-4">
        <button onClick={() => onChange(Math.max(1, current - 1))} disabled={current === 1}
          className="px-3 py-1 rounded bg-white/10 text-gray-400 disabled:opacity-40 hover:bg-white/20">Prev</button>
        <span className="text-sm text-gray-400">Page {current} of {total}</span>
        <button onClick={() => onChange(Math.min(total, current + 1))} disabled={current === total}
          className="px-3 py-1 rounded bg-white/10 text-gray-400 disabled:opacity-40 hover:bg-white/20">Next</button>
      </div>
    );
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center">
          <div className="text-5xl mb-4 animate-spin">⚙️</div>
          <div className="text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center">
          <div className="text-5xl mb-4">🔐</div>
          <div className="text-xl mb-4">Please log in to access admin dashboard</div>
          <button onClick={() => navigate('/login')} className="bg-blue-500 text-white px-6 py-2 rounded-lg">Go to Login</button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white">
        <div className="text-center">
          <div className="text-5xl mb-4">🚫</div>
          <div className="text-xl mb-2">Access Denied</div>
          <div className="text-gray-400 mb-4">You don't have admin privileges</div>
          <button onClick={() => navigate('/dashboard')} className="bg-blue-500 text-white px-6 py-2 rounded-lg">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  if (!isVerified) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white p-4">
        <div className="bg-gray-800 border border-white/10 rounded-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="text-5xl mb-4">🔐</div>
            <h2 className="text-2xl font-bold mb-2">Admin Verification</h2>
            <p className="text-gray-400 text-sm">Enter your admin PIN to continue</p>
          </div>

          {isLocked ? (
            <div className="text-center">
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-4">
                <p className="text-red-400 font-semibold">Account Locked</p>
                <p className="text-gray-400 text-sm mt-2">
                  Too many failed attempts. Try again in {lockoutTime && Math.max(0, Math.ceil((lockoutTime - new Date()) / 60000))} minutes.
                </p>
              </div>
              <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white text-sm">← Back to Dashboard</button>
            </div>
          ) : (
            <form onSubmit={handlePinSubmit}>
              <div className="mb-4">
                <input type="password" value={pinInput} onChange={(e) => setPinInput(e.target.value)}
                  placeholder="Enter PIN" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-widest placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  maxLength={10} autoFocus disabled={verifying} />
              </div>
              {pinError && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg px-4 py-2 mb-4">
                  <p className="text-red-400 text-sm text-center">{pinError}</p>
                </div>
              )}
              <button type="submit" disabled={verifying} className="w-full bg-blue-500 text-white py-3 rounded-lg font-semibold hover:bg-blue-600 transition-colors disabled:opacity-50">
                {verifying ? 'Verifying...' : 'Verify'}
              </button>
              <button type="button" onClick={() => navigate('/dashboard')} className="w-full mt-3 text-gray-400 hover:text-white text-sm py-2">← Back to Dashboard</button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <nav className="bg-black/90 border-b border-white/10 py-3 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 lg:px-4 flex items-center justify-between">
          <div className="flex items-center gap-2 lg:gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white transition-colors text-sm">
              ← <span className="hidden sm:inline">Back</span>
            </button>
            <div className="flex items-center gap-2">
              <span className="text-xl lg:text-2xl">⚙️</span>
              <span className="text-base lg:text-xl font-bold hidden sm:inline">Admin Dashboard</span>
              <span className="text-base font-bold sm:hidden">Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-2 lg:gap-4">
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-400">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              {user.email}
            </div>
            <button onClick={handleLogoutAdmin} className="text-xs bg-red-500/20 text-red-400 px-2 lg:px-3 py-1 rounded hover:bg-red-500/30 transition-colors">
              🔒 <span className="hidden sm:inline">Lock Admin</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-wrap gap-2 mb-4 lg:mb-6 border-b border-white/10 pb-4 overflow-x-auto">
          {[
            { id: 'overview', label: '📊', labelFull: '📊 Overview' },
            { id: 'users', label: '👥', labelFull: '👥 Users' },
            { id: 'subscriptions', label: '💳', labelFull: '💳 Subscriptions' },
            { id: 'revenue', label: '💰', labelFull: '💰 Revenue' }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-3 lg:px-4 py-2 rounded-lg font-semibold transition-all text-sm whitespace-nowrap ${
                activeTab === tab.id ? 'bg-blue-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}>
              <span className="sm:hidden">{tab.label}</span>
              <span className="hidden sm:inline">{tab.labelFull}</span>
            </button>
          ))}
          <button onClick={handleRefresh} disabled={loading}
            className="ml-auto px-3 lg:px-4 py-2 rounded-lg bg-white/5 text-gray-400 hover:bg-white/10 disabled:opacity-50 text-sm whitespace-nowrap">
            🔄 <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="text-4xl mb-4 animate-spin">⚙️</div>
            <div className="text-gray-400">Loading data...</div>
          </div>
        ) : (
          <>
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 border border-blue-500/30 rounded-xl p-3 lg:p-4">
                    <div className="text-2xl lg:text-3xl font-black text-blue-400">{stats.totalUsers}</div>
                    <div className="text-xs lg:text-sm text-gray-400">Total Users</div>
                  </div>
                  <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-xl p-3 lg:p-4">
                    <div className="text-2xl lg:text-3xl font-black text-yellow-400">{stats.proUsers}</div>
                    <div className="text-xs lg:text-sm text-gray-400">Paying Customers</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-3 lg:p-4">
                    <div className="text-2xl lg:text-3xl font-black text-green-400">{formatCurrency(stats.totalRevenue)}</div>
                    <div className="text-xs lg:text-sm text-gray-400">Total Revenue</div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-3 lg:p-4">
                    <div className="text-2xl lg:text-3xl font-black text-purple-400">
                      {stats.totalUsers > 0 ? ((stats.proUsers / stats.totalUsers) * 100).toFixed(1) : 0}%
                    </div>
                    <div className="text-xs lg:text-sm text-gray-400">Conversion Rate</div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 lg:p-6">
                    <h3 className="text-base lg:text-lg font-bold mb-3 lg:mb-4">🔥 Engagement</h3>
                    <div className="space-y-2 lg:space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">DAU (Today)</span>
                        <span className="font-semibold text-orange-400">{stats.dau}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">WAU (7 days)</span>
                        <span className="font-semibold text-yellow-400">{stats.wau}</span>
                      </div>
                      <div className="flex justify-between items-center border-t border-white/10 pt-2">
                        <span className="text-gray-400 text-sm">MAU (30 days)</span>
                        <span className="font-semibold text-green-400">{stats.mau}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 lg:p-6">
                    <h3 className="text-base lg:text-lg font-bold mb-3 lg:mb-4">📈 Growth</h3>
                    <div className="space-y-2 lg:space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Signups This Week</span>
                        <span className="font-semibold text-blue-400">{stats.signupsThisWeek}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Signups This Month</span>
                        <span className="font-semibold text-green-400">{stats.signupsThisMonth}</span>
                      </div>
                      <div className="flex justify-between items-center border-t border-white/10 pt-2">
                        <span className="text-gray-400 text-sm">Avg/Day (30d)</span>
                        <span className="font-semibold">{(stats.signupsThisMonth / 30).toFixed(1)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 lg:p-6">
                    <h3 className="text-base lg:text-lg font-bold mb-3 lg:mb-4">👥 User Breakdown</h3>
                    <div className="space-y-2 lg:space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Paying Customers</span>
                        <span className="font-semibold text-yellow-400">{stats.proUsers}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Active Trials</span>
                        <span className="font-semibold text-green-400">{stats.activeTrials}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Expired Trials</span>
                        <span className="font-semibold text-red-400">{stats.expiredTrials}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 lg:p-6">
                    <h3 className="text-base lg:text-lg font-bold mb-3 lg:mb-4">💰 Revenue</h3>
                    <div className="space-y-2 lg:space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">MRR</span>
                        <span className="font-semibold text-green-400">{formatCurrency(stats.monthlyRevenue + (stats.annualRevenue / 12))}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">ARR</span>
                        <span className="font-semibold text-blue-400">{formatCurrency((stats.monthlyRevenue + (stats.annualRevenue / 12)) * 12)}</span>
                      </div>
                      <div className="flex justify-between items-center border-t border-white/10 pt-2">
                        <span className="text-gray-400 text-sm">ARPU</span>
                        <span className="font-semibold">{stats.proUsers > 0 ? formatCurrency(stats.arpu) : '-'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-bold mb-4">🕐 Recent Signups</h3>
                  <div className="space-y-2">
                    {users.slice(0, 10).map(u => {
                      const trialStatus = getTrialStatus(u);
                      return (
                        <div key={u.id} className="flex items-center justify-between py-2 border-b border-white/5">
                          <div>
                            <div className="font-medium">{u.email}</div>
                            <div className="text-sm text-gray-500">{u.company || 'No company'}</div>
                          </div>
                          <div className="text-right">
                            <div className={`text-xs px-2 py-1 rounded-full ${u.plan_type === 'pro' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-blue-500/20 text-blue-400'}`}>
                              {u.plan_type === 'pro' ? '⭐ Pro' : '🎯 Trial'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">{formatDate(u.created_at)}</div>
                            {trialStatus && <div className={`text-xs mt-0.5 ${trialStatus.color}`}>{trialStatus.text}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="space-y-4">
                <div className="flex gap-4">
                  <input type="text" placeholder="Search by email, name, or company..." value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500" />
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm text-gray-400">User</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-400">Company</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-400">Plan</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-400">Trial Status</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-400">Joined</th>
                        <th className="text-right py-3 px-4 text-sm text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.map(u => {
                        const trialStatus = getTrialStatus(u);
                        return (
                          <tr key={u.id} className="border-t border-white/5 hover:bg-white/5">
                            <td className="py-3 px-4">
                              <div className="font-medium">{u.email}</div>
                              <div className="text-sm text-gray-500">{u.first_name} {u.last_name}</div>
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-400">{u.company || '-'}</td>
                            <td className="py-3 px-4">
                              <span className={`text-xs px-2 py-1 rounded-full ${u.plan_type === 'pro' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'}`}>
                                {u.plan_type === 'pro' ? '⭐ Pro' : '🎯 Trial'}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-sm">{trialStatus ? <span className={trialStatus.color}>{trialStatus.text}</span> : '-'}</td>
                            <td className="py-3 px-4 text-sm text-gray-400">{formatDate(u.created_at)}</td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex gap-2 justify-end flex-wrap">
                                {u.plan_type !== 'pro' ? (
                                  <>
                                    <button onClick={() => { setSelectedUser(u); setShowGrantModal(true); }}
                                      className="text-xs bg-green-500/20 text-green-400 px-3 py-1 rounded hover:bg-green-500/30 transition-colors">🎁 Grant Pro</button>
                                    <button onClick={() => handleExtendTrial(u)}
                                      className="text-xs bg-blue-500/20 text-blue-400 px-3 py-1 rounded hover:bg-blue-500/30 transition-colors">➕ Extend Trial</button>
                                  </>
                                ) : (
                                  <button onClick={() => handleRevokeAccess(u)}
                                    className="text-xs bg-red-500/20 text-red-400 px-3 py-1 rounded hover:bg-red-500/30 transition-colors">❌ Revoke</button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredUsers.length === 0 && <div className="text-center py-8 text-gray-500">No users found</div>}
                </div>
                <div className="text-sm text-gray-500">Showing {paginatedUsers.length} of {filteredUsers.length} users</div>
                <Pagination current={usersPage} total={totalUsersPages} onChange={setUsersPage} />
              </div>
            )}

            {activeTab === 'subscriptions' && (
              <div className="space-y-4">
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm text-gray-400">Email</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-400">Plan</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-400">Amount</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-400">Status</th>
                        <th className="text-left py-3 px-4 text-sm text-gray-400">Ends</th>
                        <th className="text-right py-3 px-4 text-sm text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedSubs.map(sub => (
                        <tr key={sub.id} className="border-t border-white/5 hover:bg-white/5">
                          <td className="py-3 px-4 text-sm">{sub.email}</td>
                          <td className="py-3 px-4 text-sm capitalize">{sub.plan} ({sub.billing_cycle})</td>
                          <td className="py-3 px-4 text-sm font-medium">{formatCurrency(sub.amount)}</td>
                          <td className="py-3 px-4">
                            <span className={`text-xs px-2 py-1 rounded-full ${sub.status === 'active' ? 'bg-green-500/20 text-green-400' : sub.status === 'cancelled' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-500/20 text-gray-400'}`}>
                              {sub.status}
                            </span>
                            {sub.auto_renew && <span className="ml-1 text-xs text-blue-400">🔄</span>}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-400">{formatDate(sub.ends_at)}</td>
                          <td className="py-3 px-4 text-right">
                            <button onClick={() => handleExtendSubscription(sub)}
                              className="text-xs bg-blue-500/20 text-blue-400 px-3 py-1 rounded hover:bg-blue-500/30 transition-colors">➕ Extend</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {subscriptions.length === 0 && <div className="text-center py-8 text-gray-500">No subscriptions yet</div>}
                </div>
                <Pagination current={subscriptionsPage} total={totalSubsPages} onChange={setSubscriptionsPage} />
              </div>
            )}

            {activeTab === 'revenue' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-6">
                    <div className="text-4xl font-black text-green-400">{formatCurrency(stats.totalRevenue)}</div>
                    <div className="text-sm text-gray-400 mt-1">Total Revenue</div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <div className="text-4xl font-black">{formatCurrency(stats.monthlyRevenue + (stats.annualRevenue / 12))}</div>
                    <div className="text-sm text-gray-400 mt-1">Monthly Recurring Revenue</div>
                  </div>
                  <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                    <div className="text-4xl font-black">{formatCurrency((stats.monthlyRevenue + (stats.annualRevenue / 12)) * 12)}</div>
                    <div className="text-sm text-gray-400 mt-1">Annual Run Rate</div>
                  </div>
                </div>

                <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-bold mb-4">💳 Recent Payments</h3>
                  {(() => {
                    const realPayments = subscriptions.filter(s => s.amount > 0 && !ADMIN_EMAILS.includes(s.email?.toLowerCase())).slice(0, 10);
                    if (realPayments.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-500">
                          <div className="text-3xl mb-2">💸</div>
                          <div>No payments yet. Your first customer is coming!</div>
                        </div>
                      );
                    }
                    return (
                      <div className="space-y-2">
                        {realPayments.map(sub => (
                          <div key={sub.id} className="flex items-center justify-between py-2 border-b border-white/5">
                            <div>
                              <div className="font-medium">{sub.email}</div>
                              <div className="text-sm text-gray-500 capitalize">{sub.billing_cycle} plan</div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-green-400">{formatCurrency(sub.amount)}</div>
                              <div className="text-xs text-gray-500">{formatDate(sub.created_at)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {showGrantModal && selectedUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-white/10 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">🎁 Grant Pro Access</h3>
            <p className="text-gray-300 mb-4">Grant free Pro access to <strong>{selectedUser.email}</strong></p>
            <div className="mb-6">
              <label className="block text-sm text-gray-400 mb-2">Duration (days)</label>
              <input type="number" value={grantDays} onChange={(e) => setGrantDays(parseInt(e.target.value) || 30)}
                min="1" max="365" className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500" />
              <div className="text-xs text-gray-500 mt-1">Common: 7 (trial), 30 (1 month), 365 (1 year)</div>
            </div>
            <div className="flex gap-4">
              <button onClick={() => { setShowGrantModal(false); setSelectedUser(null); }} disabled={actionLoading}
                className="flex-1 bg-white/10 text-white px-4 py-2 rounded-lg hover:bg-white/20 transition-colors">Cancel</button>
              <button onClick={handleGrantAccess} disabled={actionLoading}
                className="flex-1 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50">
                {actionLoading ? 'Granting...' : `Grant ${grantDays} Days`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;